// 화면 품질·FPS 설정(설계서 §9.5) — 뷰어 ↔ 에이전트 "ctrl" DataChannel 프로토콜(순수 로직).
//
// 🔴 에이전트 `poc/rtc-core-rust/src/quality.rs` 와 **같은 규약·같은 표**다 — 프리셋 값·FPS 목록·메시지 이름을
//    바꿀 때는 양쪽(+ Android `CtrlProtocol.kt`)을 함께 바꾼다. 값이 어긋나면 뷰어가 "높음" 이라고 표시하는데
//    에이전트는 다른 비트레이트를 쓰는, **화면으로는 절대 못 찾는** 어긋남이 된다.
//    → `quality/protocol.test.mjs` 가 Rust 쪽 표와 같은 기대값을 박아 두고 지킨다.
// 브라우저 전용 API 를 쓰지 않는다 → Node 에서 그대로 시험한다.
//
// 메시지(JSON 텍스트, 필드 t):
//   에이전트→뷰어  qcap {v, presets, fps, cur:{p,f}, native:{w,h}, rec?}   채널이 열리면 1회
//   뷰어→에이전트  q {p?, f?}                                        변경 요청(빠진 값은 현재 유지)
//   에이전트→뷰어  qok {p, f, w, h, kbps}                            **적용 결과**(요청값이 아니다)
//   에이전트→뷰어  qerr {code}
//
// 세션 녹화 고지(§9.7) — 같은 채널을 쓴다(보기 전용 세션에도 열리는 유일한 채널이고 메시지가 몇 건뿐이다):
//   뷰어→에이전트  rec {on}                                          녹화 시작/중지 **고지**
//   에이전트→뷰어  rok {on}                                          고지를 **화면에 표시했다**는 확인
//   에이전트→뷰어  rerr {code}
//
// 🔴 뷰어는 `rok` 를 받은 **뒤에** 녹화를 시작한다(§9.7.1). 이 채널의 메시지 처리는 화질 반영 확인 때문에
//    최대 2초 밀릴 수 있는데, 먼저 녹화를 시작해 버리면 **고지 없이 녹화되는 구간**이 생기기 때문이다.
//    `rok` 가 오지 않으면 녹화하지 않는다 — "고지할 수 없으면 녹화도 없다".

/** DataChannel 이름(에이전트 `CTRL_CHANNEL_LABEL`). */
export const CTRL_CHANNEL_LABEL = 'ctrl';
/** 규약 버전(qcap.v) — 뷰어가 모르는 상위 버전이면 UI 를 숨긴다. */
export const CTRL_PROTOCOL_VERSION = 1;

export const CTRL_MSG = {
  CAP: 'qcap',
  SET: 'q',
  OK: 'qok',
  ERR: 'qerr',
  /** 세션 녹화 고지(§9.7) — 뷰어 → 에이전트. */
  REC: 'rec',
  /** 고지를 화면에 표시했다는 확인 — 에이전트 → 뷰어. */
  REC_OK: 'rok',
  REC_ERR: 'rerr',
} as const;

export const CTRL_ERR = {
  BUSY: 'busy',
  UNSUPPORTED: 'unsupported',
  PROTOCOL: 'protocol',
} as const;

/** 오류 코드 → 사용자 문구. */
export const CTRL_ERR_MESSAGE: Readonly<Record<string, string>> = {
  [CTRL_ERR.BUSY]: '직전 화질 변경을 적용하는 중입니다. 잠시 뒤 다시 시도하세요.',
  [CTRL_ERR.UNSUPPORTED]: '상대 기기가 이 화질을 지원하지 않습니다.',
  [CTRL_ERR.PROTOCOL]: '화질 변경 요청이 잘못됐습니다.',
};

export type PresetId = 'low' | 'med' | 'high';

/** 품질 프리셋 정의 — 🔴 `quality.rs Preset` 과 값이 같아야 한다. */
export interface PresetDef {
  readonly id: PresetId;
  /** 화면에 보일 이름. */
  readonly label: string;
  /** 인코딩 해상도 상한(긴 변). 원본이 더 작으면 그대로 둔다(업스케일 금지). */
  readonly maxEdge: number;
  /** 비트레이트 계수(bpp) 분자/분모 — 정수 연산으로 Rust 와 값을 맞춘다. */
  readonly bppNum: number;
  readonly bppDen: number;
  /** 비트레이트 하한·상한(bps). 프리셋마다 다르다(공통 clamp 면 low 가 med 에 먹힌다). */
  readonly minBps: number;
  readonly maxBps: number;
}

export const PRESETS: readonly PresetDef[] = [
  { id: 'low', label: '낮음', maxEdge: 1280, bppNum: 1, bppDen: 20, minBps: 500_000, maxBps: 3_000_000 },
  { id: 'med', label: '보통', maxEdge: 1920, bppNum: 1, bppDen: 10, minBps: 2_000_000, maxBps: 12_000_000 },
  { id: 'high', label: '높음', maxEdge: 2560, bppNum: 3, bppDen: 20, minBps: 3_000_000, maxBps: 20_000_000 },
] as const;

/** 뷰어가 고를 수 있는 FPS 목록(에이전트 `FPS_CHOICES` 와 같다). */
export const FPS_CHOICES: readonly number[] = [5, 10, 15, 30];

/** 기본값 — 이 기능 도입 전의 동작과 같다. */
export const DEFAULT_PRESET: PresetId = 'med';
export const DEFAULT_FPS = 15;

export function presetDef(id: PresetId): PresetDef {
  const p = PRESETS.find((x) => x.id === id);
  if (!p) {
    throw new Error(`알 수 없는 프리셋: ${id}`);
  }
  return p;
}

/** 모르는 값은 null(거절) — 조용히 기본값으로 떨어뜨리지 않는다(`quality.rs Preset::parse` 와 같은 규칙). */
export function parsePreset(s: unknown): PresetId | null {
  return typeof s === 'string' && PRESETS.some((p) => p.id === s) ? (s as PresetId) : null;
}

export function validFps(f: unknown): f is number {
  return typeof f === 'number' && FPS_CHOICES.includes(f);
}

/**
 * 이 프리셋에서 주어진 **실제 인코딩 치수·FPS** 의 비트레이트(bps).
 * 🔴 `quality.rs bitrate()` 와 같은 결과여야 한다(정수 나눗셈 순서까지 동일: 곱한 뒤 나눈다).
 * 뷰어가 이 값을 직접 쓰지는 않지만, "이 설정이면 대략 몇 Mbps" 를 사용자에게 보이고
 * 자동 조절이 목표 대비 실제 수신량을 견주는 기준이 된다.
 */
export function bitrateFor(id: PresetId, width: number, height: number, fps: number): number {
  const p = presetDef(id);
  // Rust 는 u64 로 곱하고 나눈다. JS 의 Number 는 2^53 까지 정확하고 3840*2160*30*3 ≈ 7.5e8 이라 안전하다.
  const bps = Math.floor((width * height * fps * p.bppNum) / p.bppDen);
  return Math.min(Math.max(bps, p.minBps), p.maxBps);
}

/**
 * 원본 치수에 이 프리셋을 적용했을 때의 인코딩 치수(종횡비 유지·짝수 내림·**업스케일 금지**).
 * 🔴 `convert::target_dims(sw, sh, max_edge)` 와 같은 계산이다 — 뷰어가 "높음을 골라도 해상도는 그대로"
 * 라고 미리 말해 줄 수 있어야 한다. 아니면 "바꿨는데 안 커진다" 로 읽힌다.
 */
export function encodedDims(
  id: PresetId,
  srcW: number,
  srcH: number,
): { width: number; height: number } {
  const maxEdge = presetDef(id).maxEdge;
  const even = (v: number) => v & ~1;
  const dw = Math.max(even(Math.min(srcW, maxEdge)), 2);
  const dh = Math.max(even(Math.floor((srcH * dw) / Math.max(srcW, 1))), 2);
  return { width: dw, height: dh };
}

/** 이 화면에서 프리셋을 올려도 해상도가 오르지 않는가(= 원본이 이미 상한 이하). UI 문구용. */
export function resolutionCapped(id: PresetId, srcW: number): boolean {
  return srcW <= presetDef(id).maxEdge;
}

export interface QualitySpec {
  preset: PresetId;
  fps: number;
}

/** 에이전트가 채널을 열며 보내는 능력 통지. */
export interface QualityCaps {
  version: number;
  presets: readonly PresetId[];
  fps: readonly number[];
  current: QualitySpec;
  /** 축소 전 원본 화면 치수(없을 수 있다 — 구현이 못 알아낸 경우). */
  native?: { width: number; height: number };
  /**
   * 이 에이전트가 **녹화 고지를 화면에 표시할 수 있는가**(§9.7.2).
   * 구버전은 이 필드가 없다 → false → 뷰어가 녹화 단추를 **숨긴다**.
   * "고지할 수 없으면 녹화도 없다" 가 이 기능의 안전장치이므로, 모르면 못 한다고 본다.
   */
  recording: boolean;
}

/** 에이전트가 보내는 적용 결과. */
export interface QualityApplied extends QualitySpec {
  width: number;
  height: number;
  kbps: number;
}

/** `q` 요청 직렬화. 빠진 값은 보내지 않는다(에이전트가 현재 값을 유지). */
export function encodeSet(spec: Partial<QualitySpec>): string {
  const o: Record<string, unknown> = { t: CTRL_MSG.SET };
  if (spec.preset) {
    o.p = spec.preset;
  }
  if (spec.fps != null) {
    o.f = spec.fps;
  }
  return JSON.stringify(o);
}

/** `qcap` 파싱. 규약 위반이면 null(UI 를 켜지 않는다). */
export function parseCaps(v: Record<string, unknown>): QualityCaps | null {
  if (v.t !== CTRL_MSG.CAP) {
    return null;
  }
  const version = typeof v.v === 'number' ? v.v : 0;
  const presets = Array.isArray(v.presets)
    ? (v.presets.map(parsePreset).filter((p): p is PresetId => p != null) as PresetId[])
    : [];
  const fps = Array.isArray(v.fps) ? (v.fps.filter(validFps) as number[]) : [];
  const cur = (v.cur ?? {}) as Record<string, unknown>;
  const preset = parsePreset(cur.p);
  const f = cur.f;
  if (presets.length === 0 || fps.length === 0 || !preset || !validFps(f)) {
    return null;
  }
  const nat = v.native as Record<string, unknown> | undefined;
  const native =
    nat && typeof nat.w === 'number' && typeof nat.h === 'number' && nat.w > 0 && nat.h > 0
      ? { width: nat.w, height: nat.h }
      : undefined;
  // 구버전 에이전트에는 이 필드가 없다 → false(녹화 단추를 숨긴다).
  const recording = v.rec === true;
  return { version, presets, fps, current: { preset, fps: f }, native, recording };
}

/** `rec` 고지 직렬화(§9.7.2). */
export function encodeRecording(on: boolean): string {
  return JSON.stringify({ t: CTRL_MSG.REC, on });
}

/**
 * `rok` 파싱 — **고지를 표시했다**는 확인. 규약 위반이면 null.
 * 🔴 이것이 오기 전에는 녹화를 시작하지 않는다(§9.7.1).
 */
export function parseRecordingAck(v: Record<string, unknown>): { on: boolean } | null {
  if (v.t !== CTRL_MSG.REC_OK || typeof v.on !== 'boolean') {
    return null;
  }
  return { on: v.on };
}

/** `qok` 파싱. 규약 위반이면 null. */
export function parseApplied(v: Record<string, unknown>): QualityApplied | null {
  if (v.t !== CTRL_MSG.OK) {
    return null;
  }
  const preset = parsePreset(v.p);
  if (!preset || !validFps(v.f)) {
    return null;
  }
  return {
    preset,
    fps: v.f,
    width: typeof v.w === 'number' ? v.w : 0,
    height: typeof v.h === 'number' ? v.h : 0,
    kbps: typeof v.kbps === 'number' ? v.kbps : 0,
  };
}
